import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { COLORS, SPACINGS } from 'shared/styles/variables';
import styled, { keyframes } from 'styled-components';

import IconCircleBullet from 'images/icons/circle-bullet.svg';
import IconCircleChecked from 'images/icons/circle-passed.svg';
import Button, { ButtonType } from 'inquiry/components/Button';
import { IntroScreenConfig } from 'lib/interfaces/documentConfig';
import { captureMessage } from 'lib/reporting';
import PositionableButtonContainer from 'verify/components/PositionableButtonContainer/PositionableButtonContainer';
import ThemedParagraph from 'verify/components/ThemedParagraph';
import ThemedTitle from 'verify/components/ThemedTitle';
import { DocumentConfigWithSubmitted } from 'verify/redux/selectors/documents';
import { MIN_HEIGHTS } from 'verify/styles/variables';

const Header = styled('div')`
  min-height: ${MIN_HEIGHTS.header};
  margin-bottom: ${SPACINGS.base * 4}px;
`;

const DocumentsList = styled('div')`
  margin-bottom: ${SPACINGS.base * 4}px;
  padding: 0 ${SPACINGS.base}px 0 ${SPACINGS.base}px;
`;

const DocumentsListItem = styled('div')``;

const DocumentDescription = styled('div')`
  display: flex;

  margin-bottom: ${SPACINGS.base * 2}px;
`;

const IconContainer = styled('div')`
  padding: 4px ${SPACINGS.base}px 0 0;
  position: relative;
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  0% {
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
`;

const AnimatedIconCircleBullet = styled(IconCircleBullet)`
  animation: ${fadeOut} 0.5s ease-in;
  animation-fill-mode: forwards;
`;

const AnimatedIconCircleChecked = styled(IconCircleChecked)`
  animation: ${fadeIn} 0.5s ease-in;
  position: absolute;
  transform: translate(-100%, 0);

  path {
    transition-duration: 150ms;
    stroke: ${COLORS.green100};
  }
`;

const DocumentExample = styled('div')`
  display: flex;

  padding-left: ${SPACINGS.base * 3}px;
  margin-bottom: ${SPACINGS.base * 2}px;
`;

interface VerifyDocumentIntroViewProps {
  handleContinue: () => void;
  isLoading: boolean;
  isProcessing: boolean;
  introScreenConfig?: IntroScreenConfig;
  documentConfigs: DocumentConfigWithSubmitted[];
}

export default function VerifyDocumentsIntroView(props: VerifyDocumentIntroViewProps) {
  const { handleContinue, isLoading, isProcessing, introScreenConfig, documentConfigs } = props;

  const [t] = useTranslation();

  useEffect(() => {
    if (documentConfigs.length === 0) {
      captureMessage('Unexpected empty list of required documents in step config');
    }
  }, []);

  return (
    <div>
      <Header>
        <ThemedTitle>{introScreenConfig?.title || t('documents.intro.title')}</ThemedTitle>
        <ThemedParagraph>
          {introScreenConfig?.prompt || t('documents.intro.prompt')}
        </ThemedParagraph>
      </Header>
      <DocumentsList>
        {documentConfigs.map((dc, i) => (
          <DocumentsListItem key={`${dc.kind}-${i}`}>
            <DocumentDescription>
              <IconContainer>
                {dc.submitted ? (
                  <>
                    <AnimatedIconCircleBullet />
                    <AnimatedIconCircleChecked />
                  </>
                ) : (
                  <IconCircleBullet />
                )}
              </IconContainer>
              <p>{dc.description}</p>
            </DocumentDescription>
            {dc.examples?.map((example, j) => (
              <DocumentExample key={`${dc.kind}-${i}-example-${j}`}>
                <IconContainer>
                  <IconCircleBullet />
                </IconContainer>
                <p>{example}</p>
              </DocumentExample>
            ))}
          </DocumentsListItem>
        ))}
      </DocumentsList>
      <PositionableButtonContainer>
        <Button
          id='documents.intro__button--select'
          buttonType={ButtonType.Primary}
          onClick={handleContinue}
          disabled={isProcessing || isLoading}
          isPending={isProcessing || isLoading}
        >
          {t('documents.intro.btn-submit')}
        </Button>
      </PositionableButtonContainer>
    </div>
  );
}
